import {
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetPaymentMethodsQuery } from "src/api/services/paymentMethodsApi";
import { useIsMobile } from "src/hooks/useIsMobile";
import { desktopSx, mobileSx } from "src/utils/styles/modalStyles";
import CloseIcon from "@mui/icons-material/Close";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { usePayInvoiceNowMutation } from "src/api/services/paymentsApi";
import { useNavigate } from "react-router-dom";
import { Paths } from "src/constants/paths";
import { useTranslation } from "react-i18next";
import { isApiError } from "src/utils/isApiError";

interface ModalProps {
  open: boolean;
  invoiceId: string | null;
  onClose: (o: boolean) => void;
  refetchInvoices: () => void;
}

const PayInvoiceModal = ({
  open,
  onClose,
  invoiceId,
  refetchInvoices,
}: ModalProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [payInvoice] = usePayInvoiceNowMutation();
  const { data: paymentMethods } = useGetPaymentMethodsQuery();
  const component = "transaction-history.pay-invoice-modal";

  const methods = useMemo(() => {
    if (paymentMethods == null) return [];

    return paymentMethods;
  }, [paymentMethods]);

  const defaultPaymentMethod = useMemo(
    () => methods.find((method) => method.is_default),
    [methods],
  );

  const [selectedMethod, setSelectedMethod] = useState<string>();

  const handleChangeMethod = useCallback((e: SelectChangeEvent<string>) => {
    setSelectedMethod(e.target.value);
  }, []);

  const handlePayInvoice = useCallback(async () => {
    if (invoiceId == null || selectedMethod == null) return null;

    setLoading(true);
    try {
      await payInvoice({ invoiceId, paymentMethodId: selectedMethod }).unwrap();
      refetchInvoices();
      onClose(false);
      navigate(`${Paths.MY_ACCOUNT}/${Paths.MY_ACCOUNT_TRANSACTION_HISTORY}`);
    } catch (error: unknown) {
      if (isApiError(error)) {
        setError(error.data.message);
      } else {
        setError(String(error));
      }
    } finally {
      setLoading(false);
    }
  }, [
    invoiceId,
    navigate,
    onClose,
    payInvoice,
    refetchInvoices,
    selectedMethod,
  ]);

  useEffect(() => {
    if (defaultPaymentMethod?.external_id != null) {
      setSelectedMethod(defaultPaymentMethod.external_id);
    }
  }, [defaultPaymentMethod]);

  return (
    <Modal open={open} onClose={onClose}>
      <Stack sx={isMobile ? mobileSx : desktopSx} gap={2}>
        <Stack>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant={isMobile ? "body1" : "h6"} fontWeight={600}>
              {t(`${component}.header`, "Charge default card or add new card")}
            </Typography>
            <IconButton size="small" onClick={() => onClose(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Typography variant={isMobile ? "subtitle2" : "subtitle1"}>
            {t(`${component}.subheader`)}
          </Typography>
        </Stack>
        <FormControl fullWidth>
          <Select value={selectedMethod} onChange={handleChangeMethod}>
            {methods.map((method) => (
              <MenuItem key={method.id} value={method.external_id ?? undefined}>
                <Stack direction="row" gap={isMobile ? 2 : 4}>
                  <Typography>{method.card_brand}</Typography>
                  <Stack direction="row" gap={1}>
                    <Typography>
                      **** **** ****{method.last_4_digits}
                    </Typography>
                    <Typography>{method.expiration_date}</Typography>
                  </Stack>
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {error ? (
          <Typography sx={{ color: "red" }} textAlign="center">
            {t(`${component}.error-message`, error)}
          </Typography>
        ) : null}
        <LoadingButton
          loading={loading}
          onClick={handlePayInvoice}
          disabled={!selectedMethod}
        >
          {t(`${component}.submit`, "Charge card")}
        </LoadingButton>
      </Stack>
    </Modal>
  );
};

export default PayInvoiceModal;
